import type { Dispatch } from '@reduxjs/toolkit';
import type { IVaultDocument } from 'src/legacy/@types/common';
import type {
  ICompanyState,
  IGetCompanyDocumentListQueryParams,
} from 'src/legacy/@types/company';

import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/legacy/utils/axios';

// ----------------------------------------------------------------------

const initialState: ICompanyState = {
  actionStatus: {
    create: 'none',
    delete: 'none',
    document: {
      create: 'none',
      delete: 'none',
      update: 'none',
    },
    update: 'none',
  },
  companies: [],
  error: null,
  isLoading: false,
  vault: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
  vaultActionStatus: {
    create: 'none',
    delete: 'none',
    document: {
      create: 'none',
      delete: 'none',
      update: 'none',
      upload: 'none',
    },
    update: 'none',
  },
};

const slice = createSlice({
  initialState,
  name: 'client',
  reducers: {
    // CLEAR ERROR
    clearError(state) {
      state.error = '';
    },

    createCompanyDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'failed',
        },
      };
    },

    // CREATE NEW COMPANY DOCUMENT
    createCompanyDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'loading',
        },
      };
    },

    createCompanyDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'complete',
        },
      };
      state.error = null;
    },

    createCompanyVaultDocumentFailed(state) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          create: 'failed',
        },
      };
    },

    createCompanyVaultDocumentStart(state) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          create: 'loading',
        },
      };
    },

    createCompanyVaultDocumentSuccess(state, action) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          create: 'complete',
        },
      };
      state.error = null;
    },

    deleteCompanyDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'failed',
        },
      };
    },

    // UPDATE COMPANY DOCUMENT
    deleteCompanyDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'loading',
        },
      };
    },

    deleteCompanyDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'complete',
        },
      };
      state.error = null;
    },

    deleteCompanyVaultDocumentFailed(state) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          delete: 'failed',
        },
      };
    },

    // DELETE COMPANY VAULT FILE
    deleteCompanyVaultDocumentStart(state) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          delete: 'loading',
        },
      };
    },

    deleteCompanyVaultDocumentSuccess(state, action) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          delete: 'complete',
        },
      };
      state.error = null;
    },

    // GET COMPANY DOCUMENT LIST
    getCompanyDocumentListSuccess(state, action) {
      // Todo
    },

    // GET COMPANY DOCUMENT
    getCompanyDocumentSuccess(state, action) {
      // Todo
    },

    // GET COMPANy VAULT
    getCompanyVaultSuccess(state, action) {
      state.isLoading = false;
      state.vault = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // UPDATE ACTION STATUS
    updateActionState(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        ...action.payload,
      };
    },

    updateCompanyDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'failed',
        },
      };
    },

    // UPDATE COMPANY DOCUMENT
    updateCompanyDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'loading',
        },
      };
    },

    updateCompanyDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'complete',
        },
      };
      state.error = null;

      // Todo
    },

    updateCompanyVaultFolderFailed(state) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          update: 'failed',
        },
      };
    },

    // UPDATE COMPANY DOCUMENT
    updateCompanyVaultFolderStart(state) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          update: 'loading',
        },
      };
    },

    updateCompanyVaultFolderSuccess(state, action) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          update: 'complete',
        },
      };
      state.error = null;
      const list = state.vault.results.map((doc) =>
        doc.id === action.payload.id ? action.payload : doc
      );

      state.vault = {
        ...state.vault,
        results: list,
      };
    },

    updateVaultActionState(state, action) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        ...action.payload,
      };
    },

    uploadCompanyVaultDocumentFailed(state) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          upload: 'failed',
        },
      };
    },

    // VAULT REDUCER ACTIONS //
    // CREATE NEW COMPANY VAULT FILE
    uploadCompanyVaultDocumentStart(state) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          upload: 'loading',
        },
      };
    },

    uploadCompanyVaultDocumentSuccess(state, action) {
      state.vaultActionStatus = {
        ...state.vaultActionStatus,
        document: {
          ...state.vaultActionStatus.document,
          upload: 'complete',
        },
      };
      state.error = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { clearError, updateActionState, updateVaultActionState } =
  slice.actions;

// ----------------------------------------------------------------------
// --------------          Company Document Actions        --------------
// ----------------------------------------------------------------------

export const getCompanyDocumentList = (
  companyId: string,
  params: IGetCompanyDocumentListQueryParams
) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/documents/`,
        {
          params,
        }
      );
      dispatch(
        slice.actions.getCompanyDocumentListSuccess(response.data.results)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createCompanyDocument = (companyId: string, data: FormData) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createCompanyDocumentStart());
    try {
      const response = await axios.post(
        `/organization/companies/${companyId}/documents/`,
        data,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
        }
      );
      dispatch(slice.actions.createCompanyDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.createCompanyDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getCompanyDocument = (companyId: string, documentId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/documents/${documentId}/`
      );
      dispatch(slice.actions.getCompanyDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateCompanyDocument = (
  companyId: string,
  documentId: string,
  data: FormData
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateCompanyDocumentStart());
    try {
      const response = await axios.put(
        `/organization/companies/${companyId}/documents/${documentId}/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      dispatch(slice.actions.updateCompanyDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateCompanyDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteCompanyDocument = (
  companyId: string,
  documentId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteCompanyDocumentStart());
    try {
      await axios.delete(
        `/organization/companies/${companyId}/documents/${documentId}/`
      );
      dispatch(slice.actions.deleteCompanyDocumentSuccess({ documentId }));
    } catch (error) {
      dispatch(slice.actions.deleteCompanyDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------          Company Vault Actions        --------------
// ----------------------------------------------------------------------

export const getCompanyVault = (companyId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/vault/`
      );
      const finalList = response.data.results.map((doc: IVaultDocument) => {
        return {
          ...doc,
          id: `${doc.type}_${doc.id}`,
        };
      });
      dispatch(
        slice.actions.getCompanyVaultSuccess({
          ...response.data,
          results: finalList,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const uploadCompanyVaultFile = (companyId: string, data: FormData) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.uploadCompanyVaultDocumentStart());
    try {
      const response = await axios.post(
        `/organization/companies/${companyId}/vault/files/`,
        data,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
        }
      );
      dispatch(slice.actions.uploadCompanyVaultDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.uploadCompanyVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteCompanyVaultFile = (
  companyId: string,
  documentId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteCompanyVaultDocumentStart());
    try {
      await axios.delete(
        `/organization/companies/${companyId}/vault/files/${documentId}/`
      );
      dispatch(
        slice.actions.deleteCompanyVaultDocumentSuccess({
          documentId,
          fileType: 'file',
        })
      );
    } catch (error) {
      dispatch(slice.actions.deleteCompanyVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getCompanyVaultFolderContents = (
  companyId: string,
  folderId: string
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/vault/folders/${folderId}/contents/`
      );
      const finalList = response.data.results.map((doc: IVaultDocument) => {
        return {
          ...doc,
          id: `${doc.type}_${doc.id}`,
        };
      });
      dispatch(
        slice.actions.getCompanyVaultSuccess({
          ...response.data,
          results: finalList,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createCompanyVaultFolder = (companyId: string, data: any) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createCompanyVaultDocumentStart());
    try {
      const response = await axios.post(
        `/organization/companies/${companyId}/vault/folders/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      dispatch(slice.actions.createCompanyVaultDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.createCompanyVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateCompanyVaultFolder = (
  companyId: string,
  folderId: string,
  data: any
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateCompanyVaultFolderStart());
    try {
      const response = await axios.put(
        `/organization/companies/${companyId}/vault/folders/${folderId}/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      dispatch(slice.actions.updateCompanyVaultFolderSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateCompanyVaultFolderFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteCompanyVaultFolder = (
  companyId: string,
  documentId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteCompanyVaultDocumentStart());
    try {
      await axios.delete(
        `/organization/companies/${companyId}/vault/folders/${documentId}/`
      );
      dispatch(
        slice.actions.deleteCompanyVaultDocumentSuccess({
          documentId,
          fileType: 'folder',
        })
      );
    } catch (error) {
      dispatch(slice.actions.deleteCompanyVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// export const getCompanyDocument = ( =>companyId: string, documentId: string) {
//   return async (dispatch: Dispatch) => {
//     try {
//       const response = await axios.get(
//         `/organization/companies/${companyId}/documents/${documentId}/`
//       );
//       dispatch(slice.actions.getCompanyDocumentSuccess(response.data));
//     } catch (error) {
//       dispatch(slice.actions.hasError(error));
//     }
//   };
// }

// ----------------------------------------------------------------------

// export const updateCompanyDocument = ( =>companyId: string, documentId: string, data: FormData) {
//   return async (dispatch: Dispatch) => {
//     dispatch(slice.actions.updateCompanyDocumentStart());
//     try {
//       const response = await axios.put(
//         `/organization/companies/${companyId}/documents/${documentId}/`,
//         data,
//         {
//           headers: { 'Content-Type': 'application/json' },
//         }
//       );
//       dispatch(slice.actions.updateCompanyDocumentSuccess(response.data));
//     } catch (error) {
//       dispatch(slice.actions.updateCompanyDocumentFailed());
//       dispatch(slice.actions.hasError(error));
//     }
//   };
// }

// ----------------------------------------------------------------------

// export const deleteCompanyDocument = ( =>companyId: string, documentId: number) {
//   return async (dispatch: Dispatch) => {
//     dispatch(slice.actions.deleteCompanyDocumentStart());
//     try {
//       await axios.delete(`/organization/companies/${companyId}/documents/${documentId}/`);
//       dispatch(slice.actions.deleteCompanyDocumentSuccess({ documentId }));
//     } catch (error) {
//       dispatch(slice.actions.deleteCompanyDocumentFailed());
//       dispatch(slice.actions.hasError(error));
//     }
//   };
// }
